import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { api, EditionSummary } from '../api/client';
import { useAsync, Async } from '../components/Async';
import { Flag } from '../components/TeamBadge';
import { teamName } from '../lib/flags';
import { Muted } from '../components/ui';

type MetricKey =
  | 'goals' | 'avg' | 'penalties' | 'yellowCards' | 'redCards' | 'shootouts'
  | 'attendanceAvg' | 'scorelessDraws' | 'hatTricks' | 'teams';
const METRICS: { key: MetricKey; label: string }[] = [
  { key: 'goals', label: 'Goles' },
  { key: 'avg', label: 'Prom. gol/partido' },
  { key: 'attendanceAvg', label: 'Público prom.' },
  { key: 'yellowCards', label: '🟨 Amarillas' },
  { key: 'redCards', label: '🟥 Rojas' },
  { key: 'penalties', label: 'Goles de penal' },
  { key: 'shootouts', label: 'Tandas de penales' },
  { key: 'scorelessDraws', label: 'Empates 0-0' },
  { key: 'hatTricks', label: 'Hat-tricks' },
  { key: 'teams', label: 'Equipos' },
];

const Chips = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin: 10px 0 18px;
`;
const Chip = styled.button<{ $on: boolean }>`
  border: 1.5px solid ${({ theme, $on }) => ($on ? theme.colors.accent : theme.colors.borderStrong)};
  background: ${({ theme, $on }) => ($on ? theme.colors.accent : 'transparent')};
  color: ${({ theme, $on }) => ($on ? theme.colors.accentText : theme.colors.text)};
  border-radius: 999px;
  padding: 8px 14px;
  font-size: 0.92rem;
  font-weight: 700;
`;

const COLS = '116px 1fr auto';

const CTA = styled.div`
  text-align: center;
  font-weight: 800;
  font-size: 1rem;
  color: ${({ theme }) => theme.colors.accent};
  background: ${({ theme }) => theme.colors.surfaceAlt};
  border: 1.5px dashed ${({ theme }) => theme.colors.accent};
  border-radius: 12px;
  padding: 12px;
  margin: 4px 0 14px;
`;

const HeaderRow = styled.div`
  display: grid;
  grid-template-columns: ${COLS};
  gap: 12px;
  align-items: center;
  padding: 8px 14px;
  margin-bottom: 8px;
  font-size: 0.72rem;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: ${({ theme }) => theme.colors.textMuted};
  background: ${({ theme }) => theme.colors.surfaceAlt};
  border-radius: 10px;
  .r { text-align: right; }
`;

const Card = styled.button`
  width: 100%;
  display: grid;
  grid-template-columns: ${COLS};
  align-items: center;
  gap: 12px;
  min-height: 78px;
  background: ${({ theme }) => theme.colors.surface};
  border: 1.5px solid ${({ theme }) => theme.colors.borderStrong};
  border-radius: 14px;
  padding: 14px;
  margin-bottom: 10px;
  text-align: left;

  .left { display: flex; flex-direction: column; gap: 3px; min-width: 0; }
  .left .crown { display: flex; align-items: center; gap: 6px; font-size: 1.3rem; }
  .left .yr { font-weight: 800; font-size: 1.35rem; line-height: 1; }
  .left .host { font-size: 0.78rem; color: ${({ theme }) => theme.colors.textMuted}; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
  .track { background: ${({ theme }) => theme.colors.surfaceAlt}; border-radius: 8px; height: 20px; overflow: hidden; }
  .fill { height: 100%; background: ${({ theme }) => theme.colors.accent}; border-radius: 8px; }
  .val { font-weight: 800; font-size: 1.1rem; min-width: 64px; text-align: right; }
`;

export function Historico() {
  const navigate = useNavigate();
  const [metric, setMetric] = useState<MetricKey>('goals');
  const state = useAsync(() => api.history(), [], '/history');

  return (
    <>
      <h2 style={{ marginTop: 4 }}>📚 Mundiales — histórico (1930-2022)</h2>
      <Muted>
        Compará una métrica entre las 22 ediciones (tocá una para ver su ficha completa).
        Las tarjetas existen desde 1970. No incluye córners ni posesión (no hay fuente histórica).
      </Muted>

      <Chips>
        {METRICS.map((m) => (
          <Chip key={m.key} $on={metric === m.key} onClick={() => setMetric(m.key)}>
            {m.label}
          </Chip>
        ))}
      </Chips>

      <CTA>👇 ¡ELEGÍ UN CAMPEONATO PARA VER TODA LA INFO!</CTA>

      <Async state={state}>
        {(d) => {
          const editions = [...d.editions].sort((a, b) => b.year - a.year);
          const max = Math.max(1, ...editions.map((e) => Number(e[metric] ?? 0)));
          const isAvg = metric === 'avg' || metric === 'attendanceAvg';
          const valueTitle = isAvg ? 'Promedio' : 'Total';
          return (
            <>
              <HeaderRow>
                <span>🏆 Campeón / Año / Sede</span>
                <span>{METRICS.find((m) => m.key === metric)?.label}</span>
                <span className="r">{valueTitle}</span>
              </HeaderRow>

              {editions.map((e: EditionSummary) => {
                const val = Number(e[metric] ?? 0);
                return (
                  <Card key={e.year} onClick={() => navigate(`/historico/${e.year}`)}>
                    <span className="left">
                      <span className="crown">
                        🏆 <Flag team={e.champion ?? ''} size={20} />
                      </span>
                      <span className="yr">{e.year}</span>
                      <span className="host">📍 {teamName(e.host)}</span>
                    </span>
                    <span className="track">
                      <span className="fill" style={{ width: `${(val / max) * 100}%` }} />
                    </span>
                    <span className="val">{val.toLocaleString('es-AR')}</span>
                  </Card>
                );
              })}

              <Muted style={{ marginTop: 12, fontSize: '0.75rem' }}>
                Datos:{' '}
                <a
                  href="https://github.com/jfjelstul/worldcup"
                  target="_blank"
                  rel="noreferrer"
                  style={{ textDecoration: 'underline' }}
                >
                  Fjelstul World Cup Database
                </a>{' '}
                (CC-BY-SA 4.0).
              </Muted>
            </>
          );
        }}
      </Async>
    </>
  );
}
